import { CONCEPT_VALUE_ALL_OPTION } from "../constants";
import {
  CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT,
  CLASSIFICATION_TAGGING_APPROACH_INSIGHT_ONLY,
  buildAutoTaggingPlanDimensionItem,
  type AutoTaggingPlanDimensionItem,
  type AutoTaggingPlanDimensionItemWrite,
  type AutoTaggingPlanRecord,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";
import {
  UPDATE_ASSESSMENT_RECORD_DIMENSION,
  type ClassificationPlanConfigState,
} from "./classificationPlanConfigOptions";
import type { ReferenceDimensionSelection } from "./ClassificationCascadePicker";
import {
  parseObjectSubTypeCodes,
  serializeObjectSubTypeCodes,
} from "./objectSubTypeCodes";

/** UI Execution label → API `layer` (int ≥ 0). */
export function mapExecutionToLayer(execution: string): number {
  const normalized = execution.trim().toLowerCase();
  if (normalized === "on-demand") return 1;
  // Automated (default) and unknown → layer 0
  return 0;
}

/** API `layer` → UI Execution label. */
export function mapLayerToExecution(layer: number): string {
  if (layer === 1) return "On-demand";
  if (layer >= 0) return "Automated";
  return "";
}

/** UI Tagging Approach → API `tagging_approach` (this version: insight_only only). */
export function mapTaggingApproachToApi(taggingApproach: string): string {
  const normalized = taggingApproach.trim().toLowerCase();
  if (
    normalized === "insight only" ||
    normalized === CLASSIFICATION_TAGGING_APPROACH_INSIGHT_ONLY
  ) {
    return CLASSIFICATION_TAGGING_APPROACH_INSIGHT_ONLY;
  }
  return "";
}

/** API `tagging_approach` → UI Tagging Approach label. */
export function mapTaggingApproachFromApi(taggingApproach: string): string {
  const normalized = taggingApproach.trim().toLowerCase();
  if (normalized === CLASSIFICATION_TAGGING_APPROACH_INSIGHT_ONLY) {
    return "Insight Only";
  }
  return "";
}

/** UI Assessment Template → API `assessment_type`. */
export function mapAssessmentTemplateToApi(assessmentTemplate: string): string {
  const normalized = assessmentTemplate.trim().toLowerCase();
  if (normalized === "implementation") return "implementation";
  if (normalized === "tagging") return "tagging";
  return "";
}

/** API `assessment_type` → UI Assessment Template label. */
export function mapAssessmentTemplateFromApi(assessmentType: string): string {
  const normalized = assessmentType.trim().toLowerCase();
  if (normalized === "implementation") return "Implementation";
  if (normalized === "tagging") return "Tagging";
  return "";
}

/** Build `reference_items` from multi-Dimension Reference selections. */
export function buildReferenceItemsFromDimensions(
  dimensions: readonly ReferenceDimensionSelection[],
): AutoTaggingPlanDimensionItemWrite[] {
  const items: AutoTaggingPlanDimensionItemWrite[] = [];
  for (const dim of dimensions) {
    const concept = dim.conceptCode.trim();
    if (!concept) continue;
    if (dim.allValues) {
      const item = buildAutoTaggingPlanDimensionItem({
        concept,
        allValues: true,
      });
      if (item) items.push(item);
      continue;
    }
    const item = buildAutoTaggingPlanDimensionItem({
      concept,
      allValues: false,
      values: dim.values.map((value) => ({
        code: value.code,
        name: value.name?.trim() || value.code,
      })),
    });
    if (item) items.push(item);
  }
  return items;
}

/**
 * Build `reference_items` from the Reference cascade selection.
 * Prefers `referenceDimensions` when present; else legacy single-dimension fields.
 */
export function buildReferenceItemsFromObjectSubType(options: {
  objectSubType: string;
  objectSubTypeName?: string;
  /** Selected Reference Dimension (`kind=reference`). Defaults to DocType for legacy. */
  referenceConceptCode?: string;
  referenceDimensions?: readonly ReferenceDimensionSelection[];
}): AutoTaggingPlanDimensionItemWrite[] {
  if (options.referenceDimensions && options.referenceDimensions.length > 0) {
    return buildReferenceItemsFromDimensions(options.referenceDimensions);
  }

  const codes = parseObjectSubTypeCodes(options.objectSubType);
  if (codes.length === 0) return [];

  const concept =
    (options.referenceConceptCode ?? "").trim() ||
    CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT;

  const isAll =
    codes.length === 1 &&
    codes[0]!.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.code.toLowerCase();

  if (isAll) {
    const item = buildAutoTaggingPlanDimensionItem({
      concept,
      allValues: true,
    });
    return item ? [item] : [];
  }

  const names = (options.objectSubTypeName ?? "")
    .split(",")
    .map((part) => part.trim());

  const item = buildAutoTaggingPlanDimensionItem({
    concept,
    allValues: false,
    values: codes.map((code, index) => ({
      code,
      name: names[index] || code,
    })),
  });
  return item ? [item] : [];
}

function dimensionItemToSelection(
  item: AutoTaggingPlanDimensionItem,
): ReferenceDimensionSelection {
  if (item.all_values) {
    return {
      conceptCode: item.concept,
      conceptDisplayName: item.display_name?.trim() || item.concept,
      values: [],
      allValues: true,
    };
  }
  return {
    conceptCode: item.concept,
    conceptDisplayName: item.display_name?.trim() || item.concept,
    allValues: false,
    values: item.values.map(
      (value): ConceptValue => ({
        id: value.code,
        code: value.code,
        name: value.name.trim() || value.code,
        description: value.description ?? "",
      }),
    ),
  };
}

/** Hydrate Reference cascade fields from plan `reference_items` (or legacy `sub_type`). */
export function hydrateReferenceFromPlan(plan: {
  reference_items: readonly AutoTaggingPlanDimensionItem[];
  sub_type: string;
}): {
  objectSubType: string;
  objectSubTypeName: string;
  referenceConceptCode: string;
  referenceConceptDisplayName: string;
  referenceDimensions: ReferenceDimensionSelection[];
} {
  if (plan.reference_items.length > 0) {
    const referenceDimensions = plan.reference_items.map(dimensionItemToSelection);
    const first = referenceDimensions[0]!;
    if (first.allValues) {
      return {
        objectSubType: CONCEPT_VALUE_ALL_OPTION.code,
        objectSubTypeName: CONCEPT_VALUE_ALL_OPTION.name,
        referenceConceptCode: first.conceptCode,
        referenceConceptDisplayName: first.conceptDisplayName,
        referenceDimensions,
      };
    }
    return {
      objectSubType: serializeObjectSubTypeCodes(
        first.values.map((value) => value.code),
      ),
      objectSubTypeName: first.values
        .map((value) => value.name.trim() || value.code)
        .join(", "),
      referenceConceptCode: first.conceptCode,
      referenceConceptDisplayName: first.conceptDisplayName,
      referenceDimensions,
    };
  }

  if (plan.sub_type.trim()) {
    const codes = parseObjectSubTypeCodes(plan.sub_type);
    const referenceDimensions: ReferenceDimensionSelection[] = [
      {
        conceptCode: CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT,
        conceptDisplayName: CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT,
        allValues: false,
        values: codes.map((code) => ({
          id: code,
          code,
          name: code,
        })),
      },
    ];
    return {
      objectSubType: plan.sub_type,
      objectSubTypeName: "",
      referenceConceptCode: CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT,
      referenceConceptDisplayName: CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT,
      referenceDimensions,
    };
  }

  return {
    objectSubType: "",
    objectSubTypeName: "",
    referenceConceptCode: "",
    referenceConceptDisplayName: "",
    referenceDimensions: [],
  };
}

/** Build `assessment_record_items` from Update Assessment Record multi-select. */
export function buildAssessmentRecordItems(
  selected: readonly string[],
  conceptCode?: string,
): AutoTaggingPlanDimensionItemWrite[] {
  const values = selected
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((name) => ({ code: name, name }));
  if (values.length === 0) return [];

  const concept =
    (conceptCode ?? "").trim() || UPDATE_ASSESSMENT_RECORD_DIMENSION;
  const item = buildAutoTaggingPlanDimensionItem({
    concept,
    allValues: false,
    values,
  });
  return item ? [item] : [];
}

/** Hydrate Update Assessment Record dimension + values from `assessment_record_items`. */
export function hydrateUpdateAssessmentRecordsFromPlan(
  items: readonly AutoTaggingPlanDimensionItem[],
): { concept: string; values: string[] } {
  const preferred = items.find(
    (item) =>
      item.concept.toLowerCase() ===
      UPDATE_ASSESSMENT_RECORD_DIMENSION.toLowerCase(),
  );
  const source = preferred ?? items[0];
  if (!source || source.all_values) {
    return { concept: source?.concept ?? "", values: [] };
  }
  return {
    concept: source.concept,
    values: source.values.map((value) => value.name.trim() || value.code),
  };
}

/** Map plan API fields → Classification config UI state. */
export function mapPlanConfigFromRecord(
  plan: AutoTaggingPlanRecord,
): ClassificationPlanConfigState {
  const layer =
    Number.isFinite(plan.layer) && plan.layer >= 0
      ? String(Math.floor(plan.layer))
      : "";
  const updateRecord = hydrateUpdateAssessmentRecordsFromPlan(
    plan.assessment_record_items,
  );
  return {
    taggingApproach: mapTaggingApproachFromApi(plan.tagging_approach),
    // Execution is UI-only; seed from layer so edit forms are not blank.
    execution: mapLayerToExecution(plan.layer),
    layer,
    assessmentTemplate: mapAssessmentTemplateFromApi(plan.assessment_type),
    updateAssessmentRecordConcept: updateRecord.concept,
    updateAssessmentRecords: updateRecord.values,
  };
}
